import { makeAutoObservable, runInAction } from 'mobx'
import { handleUnauthorized } from '@utils/apiHelpers'
import {
  parseOperatorOwnPresenceGetResponse,
  type OperatorOwnPresence,
} from '@utils/operatorPresence'

export class OperatorOwnPresenceStore {
  presence: OperatorOwnPresence | null = null
  loading = false
  /** GET /api/operator/presence уже выполнен в этой сессии */
  loadedOnce = false

  private loadPromise: Promise<void> | null = null

  constructor() {
    makeAutoObservable(this)
  }

  get isOnBreak(): boolean {
    return this.presence?.isOnBreak ?? false
  }

  setPresence(presence: OperatorOwnPresence) {
    this.presence = presence
  }

  applyFromResponse(json: unknown, currentUserId?: number): boolean {
    const parsed = parseOperatorOwnPresenceGetResponse(json, currentUserId)
    if (!parsed) return false
    this.setPresence(parsed)
    return true
  }

  async applyFromFetchResponse(res: Response, currentUserId?: number): Promise<boolean> {
    try {
      const json = await res.json()
      return this.applyFromResponse(json, currentUserId)
    } catch {
      return false
    }
  }

  /** Один запрос GET /api/operator/presence на загрузку приложения (пока сессия активна). */
  loadOnce(
    baseUrl: string,
    headers: Record<string, string>,
    currentUserId?: number,
  ): Promise<void> {
    if (this.loadedOnce) return Promise.resolve()
    if (this.loadPromise) return this.loadPromise

    const base = baseUrl.replace(/\/$/, '')
    if (!base) return Promise.resolve()

    this.loading = true
    this.loadPromise = (async () => {
      try {
        const res = await fetch(`${base}/api/operator/presence`, {
          headers: { 'Content-Type': 'application/json', ...headers },
        })
        if (handleUnauthorized(res)) return
        if (!res.ok) return
        const json = await res.json()
        runInAction(() => {
          this.applyFromResponse(json, currentUserId)
        })
      } catch (err) {
        console.error('[OperatorOwnPresenceStore] GET /api/operator/presence failed:', err)
      } finally {
        runInAction(() => {
          this.loading = false
          this.loadedOnce = true
          this.loadPromise = null
        })
      }
    })()

    return this.loadPromise
  }

  reset() {
    this.presence = null
    this.loading = false
    this.loadedOnce = false
    this.loadPromise = null
  }
}

export const operatorOwnPresenceStore = new OperatorOwnPresenceStore()
